import type { HardwareIconName } from '@meterup/atto';
import { ComboBox, ComboBoxItem, FieldContainer, PrimaryField } from '@meterup/atto';
import { formatBytes } from '@meterup/common';
import { useFormikContext } from 'formik';
import { type DateTimeFormatOptions, DateTime } from 'luxon';

import type { PoEInfo, SpareHardwareDevicesForNetworkQuery } from '../../gql/graphql';
import type { NOSUpgradeGroup } from '../Operator/Toolshed/NOS/Upgrades/utils';
import { paths } from '../../constants';
import { graphql } from '../../gql';
import {
  DeviceModel,
  DeviceType,
  SwitchProfileSwitchingTier,
  VirtualDeviceType,
} from '../../gql/graphql';
import { makeDrawerLink, makeLink } from '../../utils/main_and_drawer_navigation';
import { FieldProvider } from '../Form/FieldProvider';
import { AccessPointDrawerTab, AccessPointsTab } from '../Hardware/AccessPoints/enums';
import { PDUsTab } from '../Hardware/PowerDistributionUnits/enums';
import { SecurityAppliancesTab } from '../Hardware/SecurityAppliance/enums';
import { SwitchesTab } from '../Hardware/Switches/enums';
import { getLastUsedTab } from './lastUsedTabs';

// Note: Skip fetching isCurrentAsOf to avoid the expensive query
graphql(`
  fragment DeviceConfigMetadataFieldsForDeviceListBadge on DeviceConfigMetadata {
    version
    acknowledgedAt
    createdAt
    state {
      state
      timestamp
    }
    isCurrentAsOf
  }
`);

export const createVirtualDevice = graphql(`
  mutation createVirtualDevice($networkUUID: UUID!, $input: CreateVirtualDeviceInput!) {
    createVirtualDevice(networkUUID: $networkUUID, input: $input) {
      UUID
    }
  }
`);

export const updateVirtualDeviceMutation = graphql(`
  mutation UpdateVirtualDevice($uuid: UUID!, $input: UpdateVirtualDeviceInput!) {
    updateVirtualDevice(virtualDeviceUUID: $uuid, input: $input) {
      UUID
    }
  }
`);

export const deleteVirtualDeviceMutation = graphql(`
  mutation DeleteVirtualDevice($uuid: UUID!) {
    deleteVirtualDevice(virtualDeviceUUID: $uuid) {
      UUID
    }
  }
`);

export const createVirtualDevicesMutation = graphql(`
  mutation createVirtualDevices($networkUUID: UUID!, $input: CreateVirtualDevicesInput!) {
    createVirtualDevices(networkUUID: $networkUUID, input: $input) {
      UUID
    }
  }
`);

export const createVirtualDevicesIndependentlyMutation = graphql(`
  mutation createVirtualDevicesIndependently(
    $networkUUID: UUID!
    $inputs: [CreateVirtualDeviceInput!]!
  ) {
    createVirtualDevicesIndependently(networkUUID: $networkUUID, input: $inputs) {
      UUID
    }
  }
`);

export const updateVirtualDevicesIndependentlyMutation = graphql(`
  mutation UpdateVirtualDevicesIndependently(
    $networkUUID: UUID!
    $inputs: [UpdateVirtualDeviceIndependentlyInput!]!
  ) {
    updateVirtualDevicesIndependently(networkUUID: $networkUUID, inputs: $inputs) {
      UUID
    }
  }
`);

export const deviceTypeForVirtualDeviceType: Record<VirtualDeviceType, DeviceType> = {
  [VirtualDeviceType.Controller]: DeviceType.Controller,
  [VirtualDeviceType.Switch]: DeviceType.Switch,
  [VirtualDeviceType.AccessPoint]: DeviceType.AccessPoint,
  [VirtualDeviceType.Observer]: DeviceType.AccessPoint,
  [VirtualDeviceType.PowerDistributionUnit]: DeviceType.PowerDistributionUnit,
};

export const iconForDeviceType: Record<DeviceType, HardwareIconName> = {
  [DeviceType.Controller]: 'security-appliance',
  [DeviceType.Switch]: 'switch',
  [DeviceType.AccessPoint]: 'access-point',
  [DeviceType.PowerDistributionUnit]: 'power-distribution-unit',
};

export const iconForVirtualDeviceType: Record<
  VirtualDeviceType,
  Exclude<HardwareIconName, 'pdu'>
> = {
  [VirtualDeviceType.Controller]: 'security-appliance',
  [VirtualDeviceType.Switch]: 'switch',
  [VirtualDeviceType.AccessPoint]: 'access-point',
  [VirtualDeviceType.Observer]: 'access-point', // TODO: Add observer hardware icon
  [VirtualDeviceType.PowerDistributionUnit]: 'power-distribution-unit',
};

export const drawerPathForDeviceType: Record<
  DeviceType,
  (companyName: string, networkSlug: string, uuid: string) => ReturnType<typeof makeDrawerLink>
> = {
  [DeviceType.AccessPoint]: (companyName, networkSlug, uuid) =>
    makeDrawerLink(window.location, paths.drawers.AccessPointDrawerPage, {
      companyName,
      networkSlug,
      uuid,
      tab: AccessPointDrawerTab.AccessPoint,
    }),
  [DeviceType.Controller]: (companyName, networkSlug, uuid) =>
    makeDrawerLink(window.location, paths.drawers.SecurityApplianceDrawerPage, {
      companyName,
      networkSlug,
      uuid,
    }),
  [DeviceType.Switch]: (companyName, networkSlug, uuid) =>
    makeDrawerLink(window.location, paths.drawers.SwitchSummaryPage, {
      companyName,
      networkSlug,
      uuid,
    }),
  [DeviceType.PowerDistributionUnit]: (companyName, networkSlug, uuid) =>
    makeDrawerLink(window.location, paths.drawers.PowerDistributionUnitDrawerPage, {
      companyName,
      networkSlug,
      uuid,
    }),
};

export const indexPathForVirtualDeviceType: Record<
  VirtualDeviceType,
  (companyName: string, networkSlug: string) => ReturnType<typeof makeLink>
> = {
  [VirtualDeviceType.AccessPoint]: (companyName, networkSlug) =>
    makeLink(paths.pages.AccessPointsListPage, {
      companyName,
      networkSlug,
      tab: AccessPointsTab.Current,
    }),
  [VirtualDeviceType.Observer]: (companyName, networkSlug) =>
    makeLink(paths.pages.ObserversListPage, {
      companyName,
      networkSlug,
    }),
  [VirtualDeviceType.Controller]: (companyName, networkSlug) =>
    makeLink(paths.pages.SecurityAppliancesListPage, {
      companyName,
      networkSlug,
      tab: SecurityAppliancesTab.Current,
    }),
  [VirtualDeviceType.Switch]: (companyName, networkSlug) =>
    makeLink(paths.pages.SwitchesPage, {
      companyName,
      networkSlug,
      tab: SwitchesTab.Current,
    }),
  [VirtualDeviceType.PowerDistributionUnit]: (companyName, networkSlug) =>
    makeLink(paths.pages.PowerDistributionUnitListPage, {
      companyName,
      networkSlug,
      tab: PDUsTab.Current,
    }),
};

export const pagePathForVirtualDeviceType: Record<
  VirtualDeviceType,
  (companyName: string, networkSlug: string, uuid: string) => ReturnType<typeof makeLink>
> = {
  [VirtualDeviceType.AccessPoint]: (companyName, networkSlug, uuid) =>
    makeLink(paths.pages.AccessPointPage, {
      companyName,
      networkSlug,
      uuid,
      tab: getLastUsedTab(VirtualDeviceType.AccessPoint),
    }),
  [VirtualDeviceType.Observer]: (companyName, networkSlug, uuid) =>
    makeLink(paths.pages.ObserverPage, {
      companyName,
      networkSlug,
      uuid,
      tab: getLastUsedTab(VirtualDeviceType.Observer),
    }),
  [VirtualDeviceType.Controller]: (companyName, networkSlug, uuid) =>
    makeLink(paths.pages.SecurityAppliancePage, {
      companyName,
      networkSlug,
      uuid,
      tab: getLastUsedTab(VirtualDeviceType.Controller),
    }),
  [VirtualDeviceType.Switch]: (companyName, networkSlug, uuid) =>
    makeLink(paths.pages.SwitchDetailPage, {
      companyName,
      networkSlug,
      uuid,
      tab: getLastUsedTab(VirtualDeviceType.Switch),
    }),
  [VirtualDeviceType.PowerDistributionUnit]: (companyName, networkSlug, uuid) =>
    makeLink(paths.pages.PowerDistributionUnitDetailPage, {
      companyName,
      networkSlug,
      uuid,
      tab: getLastUsedTab(VirtualDeviceType.PowerDistributionUnit),
    }),
};

export const deviceTypeModels: Record<DeviceType, DeviceModel[]> = {
  [DeviceType.Controller]: [
    DeviceModel.Mc05,
    DeviceModel.Mc06,
    DeviceModel.Mc11,
    DeviceModel.F1,
    DeviceModel.F2,
    DeviceModel.F3,
  ],
  [DeviceType.AccessPoint]: [
    DeviceModel.Mw03,
    DeviceModel.Mw04,
    DeviceModel.Mw05,
    DeviceModel.Mw06,
    DeviceModel.Mw07,
    DeviceModel.Mw08,
    DeviceModel.Mw09,
    DeviceModel.A1,
    DeviceModel.A2,
  ],
  [DeviceType.Switch]: [
    DeviceModel.Ms10,
    DeviceModel.Ms11,
    DeviceModel.Ms12,
    DeviceModel.S1,
    DeviceModel.S2,
    DeviceModel.S3,
  ],
  [DeviceType.PowerDistributionUnit]: [DeviceModel.Mp01, DeviceModel.P1],
} as const;

export const defaultDeviceTypeModels: Record<DeviceType, DeviceModel> = {
  [DeviceType.Controller]: DeviceModel.Mc06,
  [DeviceType.Switch]: DeviceModel.Ms10,
  [DeviceType.AccessPoint]: DeviceModel.Mw08,
  [DeviceType.PowerDistributionUnit]: DeviceModel.Mp01,
} as const;

export const defaultSwitchModels: Record<SwitchProfileSwitchingTier, DeviceModel> = {
  [SwitchProfileSwitchingTier.Access]: DeviceModel.Ms10,
  [SwitchProfileSwitchingTier.Core]: DeviceModel.Ms12,
  [SwitchProfileSwitchingTier.Distribution]: DeviceModel.Ms12,
  [SwitchProfileSwitchingTier.Isp]: DeviceModel.Ms10,
} as const;

export const defaultSwitchSTPBridgePriority: Record<SwitchProfileSwitchingTier, number | null> = {
  [SwitchProfileSwitchingTier.Access]: 28672,
  [SwitchProfileSwitchingTier.Core]: 0,
  [SwitchProfileSwitchingTier.Distribution]: 8192,
  [SwitchProfileSwitchingTier.Isp]: null,
} as const;

export const deviceTypeToLabelMap: Record<DeviceType, string> = {
  [DeviceType.AccessPoint]: 'Access point',
  [DeviceType.Controller]: 'Security appliance',
  [DeviceType.Switch]: 'Switch',
  [DeviceType.PowerDistributionUnit]: 'Power distribution unit',
} as const;

export const virtualDeviceTypeToLabelMap: Record<VirtualDeviceType, string> = {
  [VirtualDeviceType.AccessPoint]: 'Access point',
  [VirtualDeviceType.Controller]: 'Security appliance',
  [VirtualDeviceType.Switch]: 'Switch',
  [VirtualDeviceType.PowerDistributionUnit]: 'Power distribution unit',
  [VirtualDeviceType.Observer]: 'Observer',
} as const;

export const deviceTypeToLabelPrefix: Record<DeviceType, string> = {
  [DeviceType.AccessPoint]: 'AP',
  [DeviceType.Controller]: '',
  [DeviceType.Switch]: '',
  [DeviceType.PowerDistributionUnit]: 'PDU',
} as const;

export function getDeviceTypesForAlertArgs(): { value: string; label: string }[] {
  return Object.values(VirtualDeviceType).reduce(
    (acc, deviceType) => {
      const lowercasedDeviceType = deviceType.toLowerCase();
      acc.push({
        value: lowercasedDeviceType,
        label: virtualDeviceTypeToLabelMap[deviceType],
      });
      return acc;
    },
    [] as { value: string; label: string }[],
  );
}

export function deviceTypeLabelPlural(
  maybeVirtualDeviceType: DeviceType | VirtualDeviceType,
): string {
  if (maybeVirtualDeviceType === VirtualDeviceType.Observer) {
    return 'Observers';
  }
  const deviceType = deviceTypeForVirtualDeviceType[maybeVirtualDeviceType];

  switch (deviceType) {
    case DeviceType.Switch:
      return 'Switches';
  }
  return `${deviceTypeToLabelMap[deviceType]}s`;
}

export const assignHardwareDeviceToVirtualDevice = graphql(`
  mutation AssignHardwareDeviceToVirtualDevice($serialNumber: String!, $virtualDeviceUUID: UUID!) {
    assignHardwareDeviceToVirtualDevice(
      serialNumber: $serialNumber
      virtualDeviceUUID: $virtualDeviceUUID
    ) {
      virtualDevice {
        UUID
        label
      }
    }
  }
`);

export const createDevHardwareDeviceMutation = graphql(`
  mutation CreateDevHardwareDevice($input: CreateDevHardwareDeviceInput!) {
    createDevHardwareDevice(input: $input) {
      serialNumber
    }
  }
`);

export const assignHardwareDeviceToNetwork = graphql(`
  mutation AssignHardwareDeviceToNetwork($serialNumber: String!, $networkUUID: UUID!) {
    assignHardwareDeviceToNetwork(serialNumber: $serialNumber, networkUUID: $networkUUID) {
      virtualDevice {
        UUID
        label
      }
    }
  }
`);

export const unassignHardwareDeviceFromVirtualDevice = graphql(`
  mutation UnassignHardwareDeviceFromVirtualDevice(
    $serialNumber: String!
    $assignAsSpare: Boolean! = false
  ) {
    unassignHardwareDeviceFromVirtualDevice(
      serialNumber: $serialNumber
      assignAsSpare: $assignAsSpare
    ) {
      serialNumber
    }
  }
`);

export const assignHardwareToVirtualDevicesMutation = graphql(`
  mutation AssignHardwareToVirtualDevices(
    $networkUUID: UUID!
    $inputs: [AssignHardwareDeviceToVirtualDeviceInput!]!
  ) {
    assignHardwareDevicesToVirtualDevices(networkUUID: $networkUUID, inputs: $inputs) {
      serialNumber
      virtualDevice {
        UUID
        label
      }
    }
  }
`);

export const hardwareDeviceQuery = graphql(`
  query HardwareDevice($serialNumber: String!) {
    hardwareDevice(serialNumber: $serialNumber) {
      serialNumber
      deviceType
      deviceModel

      virtualDevice {
        UUID
        label
        description
      }
    }
  }
`);

export const updateHardwareDeviceMutation = graphql(`
  mutation UpdateHardwareDevice($serialNumber: String!, $input: UpdateHardwareDeviceInput!) {
    updateHardwareDevice(serialNumber: $serialNumber, input: $input) {
      serialNumber
    }
  }
`);

export const blinkLEDsMutation = graphql(`
  mutation BlinkLEDsMutation($serialNumber: String!) {
    rpcBlinkLEDs(serialNumber: $serialNumber, durationSec: 10)
  }
`);

export const refreshConfigMutation = graphql(`
  mutation RefreshConfigMutation($serialNumber: String!) {
    rpcRefreshConfig(serialNumber: $serialNumber)
  }
`);

export const clearBlockedConfigsMutation = graphql(`
  mutation ClearBlockedConfigsMutation($serialNumber: String!) {
    rpcClearBlockedConfigs(serialNumber: $serialNumber)
  }
`);

export const rpcPDUOutletCycleMutation = graphql(`
  mutation RpcPDUOutletCycleMutation($serialNumber: String!, $input: RPCPDUOutletCycleInput!) {
    rpcPDUOutletCycle(serialNumber: $serialNumber, input: $input)
  }
`);

export function formatCPULoad(usedPercentage: number) {
  return `${(usedPercentage * 100).toFixed(2)}%`;
}

export function formatMemoryUsage(usage: { bytesUsed: number; bytesAvailable: number }) {
  const memoryCapacity = usage.bytesUsed + usage.bytesAvailable;

  const percentageFree = Math.floor((usage.bytesAvailable / memoryCapacity) * 100);

  const bytesAvailable = formatBytes(usage.bytesAvailable);
  const bytesMemoryCapacity = formatBytes(memoryCapacity);
  const res = `${bytesAvailable} of ${bytesMemoryCapacity} (${percentageFree}% free)`;

  return res;
}

export function formatMemoryUsageWithTotal(usage: { bytesAvailable: number; bytestTotal: number }) {
  const percentageFree = Math.floor((usage.bytesAvailable / usage.bytestTotal) * 100);
  const bytesAvailable = formatBytes(usage.bytesAvailable);
  const bytesMemoryCapacity = formatBytes(usage.bytestTotal);
  const res = `${bytesAvailable} of ${bytesMemoryCapacity} (${percentageFree}% free)`;

  return res;
}

export function formatPoEInfo(poeInfo: Pick<PoEInfo, 'max' | 'used'> | null | undefined) {
  if (poeInfo?.used == null || poeInfo?.max == null) return 'N/A';
  const percentageUsed = poeInfo.max ? Math.floor((poeInfo.used / poeInfo.max) * 100) : 0;
  const res = `${poeInfo.used.toFixed(2)} of ${poeInfo.max} Watts (${percentageUsed}%)`;

  return res;
}

export function formatTimestamp(
  isoTimestamp: string,
  formatOptions?: DateTimeFormatOptions | 'relative',
): string {
  const localTime = DateTime.fromISO(isoTimestamp).toLocal();
  if (formatOptions === 'relative') {
    return localTime.toRelative({ style: 'long' }) ?? '';
  }
  return localTime.toLocaleString(formatOptions ?? DateTime.DATETIME_FULL);
}

export const LatestDeviceMemoryUsageQuery = graphql(`
  query LatestDeviceMemoryUsage($serialNumber: String!) {
    latestDeviceMemoryUsage(serialNumber: $serialNumber) {
      bytesUsed
      bytesAvailable
    }
  }
`);

export const DeviceUptimeQuery = graphql(`
  query DeviceUptime($serialNumber: String!) {
    deviceUptime(serialNumber: $serialNumber)
  }
`);

export const LatestDeviceCPULoadQuery = graphql(`
  query LatestDeviceCPULoad($serialNumber: String!) {
    latestDeviceCPULoad(serialNumber: $serialNumber) {
      usedPercentage
    }
  }
`);

export const switchPoEInfoQuery = graphql(`
  query SwitchPoEInfo($serialNumber: String!) {
    hardwareDevice(serialNumber: $serialNumber) {
      __typename
      ... on SwitchHardwareDevice {
        poeInfo {
          max
          used
        }
      }
    }
  }
`);

export const switchVirtualDevicePoEInfoQuery = graphql(`
  query SwitchVirtualDevicePoEInfo($uuid: UUID!) {
    virtualDevice(UUID: $uuid) {
      __typename
      ... on SwitchVirtualDevice {
        poeInfo {
          max
          used
        }
      }
    }
  }
`);

export const SystemInfoStatQuery = graphql(`
  query SystemInfoStatQuery($uuid: UUID!) {
    systemInfoStatForVirtualDevice(
      virtualDeviceUUID: $uuid
      filter: { durationSeconds: 300, limit: 1 }
    ) {
      uptime
      memoryBytesAvailable
      memoryBytesTotal
      cpuUsedPercentage
    }
  }
`);

export const updatePhyInterfaceMutation = graphql(`
  mutation UpdatePhyInterface($uuid: UUID!, $input: UpdatePhyInterfaceInput!) {
    updatePhyInterface(UUID: $uuid, input: $input) {
      UUID
    }
  }
`);

export const updatePhyInterfacesMutation = graphql(`
  mutation UpdatePhyInterfaces(
    $phyInterfaceUUIDs: [UUID!]!
    $virtualDeviceUUID: UUID!
    $input: UpdatePhyInterfaceInput!
  ) {
    updatePhyInterfaces(
      phyInterfaceUUIDs: $phyInterfaceUUIDs
      virtualDeviceUUID: $virtualDeviceUUID
      input: $input
    ) {
      UUID
    }
  }
`);

export const updatePhyInterfacesIndependentlyMutation = graphql(`
  mutation UpdatePhyInterfacesIndependently($inputs: [UpdatePhyInterfacesIndependentlyInput!]!) {
    updatePhyInterfacesIndependently(inputs: $inputs) {
      UUID
    }
  }
`);

export const spareHardwareDevicesForNetwork = graphql(`
  query SpareHardwareDevicesForNetwork($networkUUID: UUID!, $filter: HardwareDevicesFilter) {
    spareHardwareDevicesForNetwork(networkUUID: $networkUUID, filter: $filter) {
      serialNumber
      deviceModel
      deviceType
    }
  }
`);

export type SpareHardwareDevice =
  SpareHardwareDevicesForNetworkQuery['spareHardwareDevicesForNetwork'][number];

export const assignHardwareDeviceAsSpare = graphql(`
  mutation AssignHardwareDeviceAsSpare($serialNumber: String!, $networkUUID: UUID!) {
    assignHardwareDeviceToNetworkAsSpare(serialNumber: $serialNumber, networkUUID: $networkUUID) {
      serialNumber
    }
  }
`);

export const unassignSpareHardwareDevice = graphql(`
  mutation UnassignSpareHardwareDevice($serialNumber: String!, $networkUUID: UUID!) {
    unassignSpareHardwareDeviceFromNetwork(serialNumber: $serialNumber, networkUUID: $networkUUID) {
      serialNumber
    }
  }
`);

export function NOSUpgradeGroupComboBoxField<Values = unknown>({
  nosUpgradeGroups,
  disableFn,
}: {
  nosUpgradeGroups: NOSUpgradeGroup[];
  disableFn?: (values: Values) => boolean;
}) {
  const { values } = useFormikContext<Values>();
  const isDisabled = disableFn?.(values);
  return (
    <FieldContainer>
      <FieldProvider name="nosUpgradeGroupUUID">
        <PrimaryField
          label="Upgrade group"
          element={
            <ComboBox disabled={isDisabled}>
              {nosUpgradeGroups.map((group) => (
                <ComboBoxItem key={group.UUID} textValue={group.name}>
                  {group.name}
                </ComboBoxItem>
              ))}
            </ComboBox>
          }
        />
      </FieldProvider>
    </FieldContainer>
  );
}

export function getDeviceStatus(
  virtualDevice:
    | {
        hardwareDevice?: { isConnectedToBackend: boolean } | null | undefined;
      }
    | null
    | undefined,
) {
  if (!virtualDevice?.hardwareDevice) return undefined;

  return virtualDevice?.hardwareDevice.isConnectedToBackend ? 'online' : 'offline';
}

export function getDeviceDrawerLink({
  deviceType,
  companySlug,
  networkSlug,
  uuid,
}: {
  companySlug: string;
  networkSlug: string;
  deviceType: VirtualDeviceType;
  uuid: string;
}) {
  switch (deviceType) {
    case VirtualDeviceType.AccessPoint:
      return makeDrawerLink(window.location, paths.drawers.AccessPointDrawerPage, {
        companyName: companySlug,
        networkSlug,
        uuid,
        tab: 'access-point',
      });
    case VirtualDeviceType.Controller:
      return makeDrawerLink(window.location, paths.drawers.SecurityApplianceDrawerPage, {
        companyName: companySlug,
        networkSlug,
        uuid,
      });
    case VirtualDeviceType.Switch:
      return makeDrawerLink(window.location, paths.drawers.SwitchSummaryPage, {
        companyName: companySlug,
        networkSlug,
        uuid,
      });
    case VirtualDeviceType.PowerDistributionUnit:
      return makeDrawerLink(window.location, paths.drawers.PowerDistributionUnitDrawerPage, {
        companyName: companySlug,
        networkSlug,
        uuid,
      });
    case VirtualDeviceType.Observer:
      // # TODO: Add observer page when we have it.
      return makeDrawerLink(window.location, paths.pages.ObserversListPage, {
        companyName: companySlug,
        networkSlug,
      });
    default:
      return undefined;
  }
}

export function HistoryTimeCell({ isoTimestamp }: { isoTimestamp: string }) {
  return <>{formatTimestamp(isoTimestamp, DateTime.DATETIME_MED_WITH_SECONDS)}</>;
}
